import { useReportDownload } from '../shared/download-report/hooks/useReportDownload'
import { useSendIncidentsToOntic } from '../shared/hooks/useSendIncidentsToOntic'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { CiLink } from 'react-icons/ci'
import { HiOutlineDotsHorizontal, HiOutlineDownload } from 'react-icons/hi'
import { IoIosSend } from 'react-icons/io'

import {
  Box,
  Menu,
  MenuButton,
  MenuList,
  Spinner,
  Tooltip,
} from '@chakra-ui/react'

import { DarkProvider, UtilityButton } from '@hivewatch/dark'

import { MenuSelectItem } from '@/components/ui'
import { IncidentQuery } from '@/graphql/generated/operations'
import {
  SEND_TO_ONTIC_DISABLED_TOOLTIP_SHOWN,
  trackEvent,
} from '@/utils/analytics'

import type { WorkflowReportDataI } from '../shared/download-report/types/types'
import { getOnticTooltipMessage, getTabColors } from '../shared/utils/helpers'

export interface AdditionalOptionsButtonIProps {
  tooltipText: string
  incident: IncidentQuery['incident']
  downloadReportTooltipLabel?: string
  isDownloadReportEnabled: boolean
  isSendToOnticEnabled: boolean
  onAssociateCaseModalOpen: () => void
  workflowReportData?: WorkflowReportDataI
}

export const AdditionalOptionsButton = ({
  downloadReportTooltipLabel = 'Incident must be resolved to download report',
  incident,
  tooltipText,
  isDownloadReportEnabled,
  isSendToOnticEnabled,
  onAssociateCaseModalOpen,
  workflowReportData,
}: AdditionalOptionsButtonIProps) => {
  const colorScheme = useLightMode()
  const { downloadReport } = useReportDownload({ incident, workflowReportData })
  const { send, isSendingToOntic } = useSendIncidentsToOntic(incident?.id)
  const isCaseManagementEnabled = useFeatureFlag(FeatureFlag.caseManagement)
  const isSendToOnticFeatureEnabled = useFeatureFlag(FeatureFlag.sendToOntic)
  const { shouldEnableCaseManagement, shouldEnableSendToOntic } =
    usePermissions()
  const shouldShowAssociateIncident =
    isCaseManagementEnabled && shouldEnableCaseManagement

  const shouldShowSendToOntic =
    isSendToOnticFeatureEnabled && shouldEnableSendToOntic

  // It's ok to hardcode this to false b/c the more menu can technically
  // never be "selected" like the other tabs can be
  const { hoverBgColor } = getTabColors(false)

  return (
    <Menu closeOnBlur={true} gutter={0} isLazy={true} matchWidth={false}>
      <Tooltip
        bg={hoverBgColor}
        color='white'
        hasArrow
        label={tooltipText}
        mt='5px'
        openDelay={250}
        p='10px'
      >
        <Box as='span' display='inline-block'>
          <DarkProvider>
            <MenuButton
              as={UtilityButton}
              colorScheme={colorScheme}
              disabled={isSendingToOntic}
              icon={
                isSendingToOntic ? (
                  <Spinner
                    emptyColor='gray.300'
                    size='sm'
                    speed='0.65s'
                    thickness='3px'
                  />
                ) : (
                  <HiOutlineDotsHorizontal size={18} />
                )
              }
              label={tooltipText}
            />
          </DarkProvider>
        </Box>
      </Tooltip>
      <MenuList p={0}>
        {shouldShowAssociateIncident && (
          <MenuSelectItem
            icon={<CiLink size={16} />}
            label='Associate Incident'
            onClick={onAssociateCaseModalOpen}
          />
        )}

        {shouldShowSendToOntic && (
          <Tooltip
            hasArrow
            isDisabled={isSendToOnticEnabled}
            label={getOnticTooltipMessage(incident)}
            onOpen={() =>
              trackEvent(SEND_TO_ONTIC_DISABLED_TOOLTIP_SHOWN, {
                incident_id: incident?.id,
                incident_status: incident?.status,
              })
            }
            placement='left'
            shouldWrapChildren
          >
            <MenuSelectItem
              icon={<IoIosSend size={16} />}
              isDisabled={!isSendToOnticEnabled}
              label='Send to Ontic'
              onClick={send}
            />
          </Tooltip>
        )}

        <Tooltip
          hasArrow
          isDisabled={isDownloadReportEnabled}
          label={downloadReportTooltipLabel}
          placement='left'
          shouldWrapChildren
        >
          <MenuSelectItem
            icon={<HiOutlineDownload size={16} />}
            isDisabled={!isDownloadReportEnabled}
            label='Download Report'
            onClick={downloadReport}
            showDivider={false}
          />
        </Tooltip>
      </MenuList>
    </Menu>
  )
}
